<template>
  <div :style="{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }">
    <!-- Save-as -->
    <div :style="{ padding: '12px 12px 13px' }">
      <div v-if="savingAs" :style="{ display: 'flex', gap: '6px' }">
        <input
          v-model="name"
          autofocus
          spellcheck="false"
          placeholder="Name this view…"
          :style="{ flex: 1, minWidth: 0, padding: '0 10px', height: '32px', borderRadius: '8px', border: '1px solid var(--p-primary-500)', background: 'var(--surface-ground, transparent)', color: 'var(--p-text-color)', fontSize: '12.5px', fontFamily: 'inherit', outline: 'none' }"
          @keydown.enter="confirmSaveAs"
          @keydown.escape="cancelSaveAs"
        >
        <button title="Save" :disabled="!canSave" :style="{ ...primaryBtn, padding: '0 12px', opacity: canSave ? 1 : 0.5 }" @click="confirmSaveAs">
          <i class="pi pi-check" :style="{ fontSize: '12px' }" />
        </button>
        <button title="Cancel" :style="{ ...ghostBtn, padding: '0 10px' }" @click="cancelSaveAs"><i class="pi pi-times" :style="{ fontSize: '12px' }" /></button>
      </div>
      <button
        v-else
        :disabled="!canSave"
        :style="{ ...primaryBtn, width: '100%', opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'default' }"
        @click="emit('update:savingAs', true)"
      >
        <i class="pi pi-bookmark" :style="{ fontSize: '12px' }" />Save as new view
      </button>
    </div>

    <div :style="{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px' }">
      <div :style="{ display: 'flex', flexDirection: 'column', gap: '4px' }">
        <!-- Default row -->
        <div v-if="def()" :style="rowStyle(def())" @click="recallView(def())">
          <i class="pi pi-bolt" :style="{ fontSize: '12px', color: def().id === activeViewId ? 'var(--p-primary-600)' : 'var(--p-surface-400)', flexShrink: 0 }" />
          <div :style="{ flex: 1, minWidth: 0 }">
            <div :style="{ fontSize: '13px', fontWeight: 700, color: 'var(--p-text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px' }">
              {{ def().name }}
              <span
                :style="{ fontSize: '8.5px', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--p-primary-700)', background: 'color-mix(in srgb, var(--p-primary-500) 16%, transparent)', padding: '1px 6px', borderRadius: '999px' }"
              >Auto-saved</span>
            </div>
            <code :style="{ fontSize: '10.5px', color: 'var(--p-surface-400)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }">{{ summarize(def()) }}</code>
          </div>
          <span v-if="countFor(def().query) != null" :style="{ fontSize: '11px', fontFamily: mono, color: 'var(--p-surface-400)', flexShrink: 0 }">{{ countFor(def().query) }}</span>
        </div>

        <div
          v-if="named().length > 0"
          :style="{ fontSize: '9.5px', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--p-surface-400)', padding: '12px 10px 4px' }"
        >Saved views</div>

        <!-- Named rows -->
        <div v-for="v in named()" :key="v.id" :style="rowStyle(v)" @click="recallView(v)">
          <i class="pi pi-bookmark-fill" :style="{ fontSize: '12px', color: v.id === activeViewId ? 'var(--p-primary-600)' : 'var(--p-surface-400)', flexShrink: 0 }" />
          <div :style="{ flex: 1, minWidth: 0 }">
            <div :style="{ fontSize: '13px', fontWeight: 700, color: 'var(--p-text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px' }">
              {{ v.name }}
              <span v-if="v.id === activeViewId && isDirty" :style="{ fontSize: '10px', fontWeight: 700, color: 'var(--p-status-warning)' }">• edited</span>
            </div>
            <code :style="{ fontSize: '10.5px', color: 'var(--p-surface-400)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }">{{ summarize(v) }}</code>
          </div>
          <span v-if="countFor(v.query) != null" :style="{ fontSize: '11px', fontFamily: mono, color: 'var(--p-surface-400)', flexShrink: 0 }">{{ countFor(v.query) }}</span>
          <button
            title="Delete view"
            :style="{ background: 'transparent', border: 0, padding: '3px', cursor: 'pointer', color: 'var(--p-surface-400)', display: 'flex' }"
            @click.stop="deleteView(v.id)"
          >
            <i class="pi pi-trash" :style="{ fontSize: '11px' }" />
          </button>
        </div>

        <div
          v-if="named().length === 0"
          :style="{ padding: '16px 12px', textAlign: 'center', fontSize: '12px', color: 'var(--p-surface-400)', lineHeight: 1.5 }"
        >No saved views yet. Tune the search, layout, or fields — then “Save as new view”.</div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Saved-views tab: the auto-saved Default plus named snapshots, each with a
// live match count. Ported from list-edit-shell.jsx SavedViews. Recall / save /
// delete are passed in as callbacks; `savingAs` is parent-controlled.
import { ref } from 'vue'
import { FqlToMqlUtilities } from '@frankai/fql'
import { matchDoc } from '@/admin-collection-layout/composables/fqlSearchHelpers.js'
import { mono } from '../search/bits.js'

const props = defineProps({
  data: { type: Array, default: () => [] },
  fieldDefinitions: { type: Object, required: true },
  views: { type: Array, default: () => [] },
  activeViewId: { type: String, default: 'default' },
  isDirty: { type: Boolean, default: false },
  canSave: { type: Boolean, default: false },
  savingAs: { type: Boolean, default: false },
  recallView: { type: Function, required: true },
  saveAsNew: { type: Function, required: true },
  deleteView: { type: Function, required: true },
})
const emit = defineEmits(['update:savingAs'])

const name = ref('')
function countFor(query) {
  try {
    const filter = FqlToMqlUtilities.convert(query || '', props.fieldDefinitions, { posture: 'interactive' }).filter
    return props.data.reduce((n, d) => n + (matchDoc(d, filter) ? 1 : 0), 0)
  } catch {
    return null
  }
}
function confirmSaveAs() {
  if (!props.canSave) return
  props.saveAsNew(name.value.trim() || null)
  name.value = ''
  emit('update:savingAs', false)
}
function cancelSaveAs() {
  name.value = ''
  emit('update:savingAs', false)
}
const def = () => props.views.find((v) => v.isDefault) || null
const named = () => props.views.filter((v) => !v.isDefault)
const summarize = (v) => (v.query && v.query.trim() ? v.query : 'No filters · all records')

const btnBase = {
  height: '32px',
  borderRadius: '8px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '12.5px',
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '7px',
  whiteSpace: 'nowrap',
}
const primaryBtn = { ...btnBase, border: '1px solid var(--p-primary-500)', background: 'var(--p-primary-500)', color: 'var(--p-primary-950)' }
const ghostBtn = { ...btnBase, border: '1px solid var(--p-content-border-color)', background: 'transparent', color: 'var(--p-text-muted-color)' }

const rowStyle = (v) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '9px 10px',
  borderRadius: '9px',
  cursor: 'pointer',
  border: `1px solid ${v.id === props.activeViewId ? 'var(--p-primary-500)' : 'transparent'}`,
  background: v.id === props.activeViewId ? 'var(--p-highlight-background)' : 'transparent',
})
</script>
